import { useEffect, useRef } from "react";
import { getSyncSidebarStatus } from "../lib/api";
import { useToast } from "./useToast";
import { useSyncStore } from "./useSyncStore";
import { useAuth } from "./useAuth";
import { hasNewSuccessfulSync, shouldRetainTransientActive } from "./syncWatcherLogic";

const SESSION_KEY = "alta_last_successful_sync_job_v2";

export function useSyncWatcher(): void {
  const { user } = useAuth();
  const canWatchSync = user?.role === "admin" || user?.role === "berater";
  const prevActiveJobId = useRef<string | null | undefined>(undefined);
  const consecutiveEmptyPolls = useRef(0);
  useEffect(() => {
    if (!canWatchSync) {
      prevActiveJobId.current = undefined;
      useSyncStore.getState().setActiveJob(null);
      useSyncStore.getState().setLastCompletedJob(null);
      useSyncStore.getState().setWorkerPaused(false);
      useSyncStore.getState().setStatusPhase("ready");
      consecutiveEmptyPolls.current = 0;
      return;
    }

    useSyncStore.getState().setStatusPhase("loading");
    let timeoutId: ReturnType<typeof setTimeout>;
    let cancelled = false;

    async function poll() {
      if (cancelled) return;

      let activeJobId: string | null = null;
      let latestCompletedId: string | null = null;

      try {
        const data = await getSyncSidebarStatus();
        activeJobId = data.active_job?.job_id ?? null;
        latestCompletedId = data.last_completed_job?.job_id ?? null;

        const previous = useSyncStore.getState().activeJob;
        if (data.active_job) {
          consecutiveEmptyPolls.current = 0;
          useSyncStore.getState().setActiveJob(data.active_job);
        } else {
          consecutiveEmptyPolls.current += 1;
          const retainTransientRunning = shouldRetainTransientActive(
            previous,
            data.last_completed_job?.job_id ?? null,
            consecutiveEmptyPolls.current,
            Date.now(),
          );
          if (!retainTransientRunning) {
            useSyncStore.getState().setActiveJob(null);
          } else {
            activeJobId = previous!.job_id;
          }
        }
        useSyncStore.getState().setWorkerPaused(data.worker_paused ?? false);
        useSyncStore.getState().setLastCompletedJob(data.last_completed_job ?? null);
        useSyncStore.getState().setStatusPhase("ready");
      } catch {
        useSyncStore.getState().setStatusPhase("error");
        scheduleNext(60_000);
        return;
      }

      if (cancelled) return;

      const storedCompletedId = sessionStorage.getItem(SESSION_KEY);
      const firstPoll = prevActiveJobId.current === undefined;

      if (!firstPoll) {
        const completedViaNewSuccessfulJob = hasNewSuccessfulSync(
          firstPoll,
          latestCompletedId,
          storedCompletedId,
        );

        if (completedViaNewSuccessfulJob) {
          if (latestCompletedId !== null) {
            sessionStorage.setItem(SESSION_KEY, latestCompletedId);
          }
          useToast.getState().success(
            "Sync abgeschlossen – neue Daten beim nächsten Seitenwechsel verfügbar",
          );
        }
      } else if (latestCompletedId !== null && storedCompletedId === null) {
        sessionStorage.setItem(SESSION_KEY, latestCompletedId);
      }

      prevActiveJobId.current = activeJobId;
      scheduleNext(activeJobId !== null ? 15_000 : 60_000);
    }

    function scheduleNext(delay: number) {
      if (!cancelled) {
        timeoutId = setTimeout(poll, delay);
      }
    }

    // The sidebar must not render an authoritative "no sync" before its first
    // authenticated status response. This request is small and starts only
    // after useAuth has exposed an eligible user.
    void poll();

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [canWatchSync]);
}
